import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router";

import { AppDispatch, RootState } from "../../store";
import { orderActions } from "../../store/slices/OrderSlice";
import Event, { SelectedService } from "../../ts/models/Event";
import { BASE_URL, CONTACT_DETAILS_URL, POD_URL } from "../../Routes/urls";
import { fetchEvent, fetchEventRegistrationCount } from "../../services/events";
import { RegistrationCount } from "../../ts/types";
import { setCurrEvent } from "../../store/actions/EventActions";
import { warningActions } from "../../store/slices/WarningSlice";
import { getPatientDetailsFromPortal } from "../../services/orders";
import Card from "../../components/Card/Card";
import ProductsCard from "./ProductSelectionCard";
import classes from "./ProductSelection.module.css";
import EventDetailsCard from "../Event/EventDetailsCard/EventDetailsCard";
import PrimaryButton from "../../components/Buttons/PrimaryButton";
import PatientLogin from "./PatientLogin/PatientLogin";
import Header from "../Header/Header";
import SelectMember from "./PatientLogin/SelectMember";
import { getProductInfoQuestions } from "../../store/actions/PODActions";

const ProductSelection = () => {
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const { podId, eventId } = useParams();
  const url = new URL(window.location.href);
  const day = url.searchParams.get("day");
  const searchParams = new URLSearchParams(window.location.search);
  const patientId = searchParams.get("p_id");
  const token = searchParams.get("t_id");

  const { servicesProductsOfCurrEvent, currEvent } = useSelector(
    (state: RootState) => state.events
  );
  const { selectedServiceProducts } = useSelector(
    (state: RootState) => state.order
  );
  const services = servicesProductsOfCurrEvent.map(
    (servicesProducts) => servicesProducts.service
  );
  const resourceName = useSelector(
    (state: RootState) => state.pod.resourceName
  );
  const servicesData = useSelector(
    (state: RootState) => state.masters.services
  );

  const [selectedServiceProductList, setSelectedServiceProductList] = useState<
    SelectedService[]
  >(selectedServiceProducts);
  const [showPatientLogin, setShowPatientLogin] = useState(false);

  const [event, setEvent] = useState<Event>();
  const [registrationCountData, setRegistrationCountData] = useState<
    RegistrationCount[]
  >([]);
  const [showMembers, setShowMembers] = useState<any>({
    show: false,
    data: [],
  });

  useEffect(() => {
    if (token && patientId && localStorage.getItem("patientData")) {
      localStorage.removeItem("patientData");
      localStorage.removeItem("isPatientPortalData");
    }
  }, [token, patientId]);

  const proceedHandler = async () => {
    dispatch(orderActions.setServicesWithProducts(selectedServiceProductList));
    getProductInfoQuestionData();

    if (currEvent?.regEmailOptional) {
      navigate(
        `${BASE_URL}/${resourceName}/${POD_URL}/${podId}/event/${eventId}/${CONTACT_DETAILS_URL}${window.location.search}`
      );
      return;
    }

    if (localStorage.getItem("patientData")) {
      navigate(
        `${BASE_URL}/${resourceName}/${POD_URL}/${podId}/event/${eventId}/${CONTACT_DETAILS_URL}${window.location.search}`
      );
    } else {
      if (patientId && token) {
        try {
          const res = await getPatientDetailsFromPortal({ patientId, token });
          if (res.status === 200) {
            if (res.data.errorCode === "bad_credentials") {
              dispatch(
                warningActions.showWarning({
                  message:
                    "Sorry, we could not retrieve your details from the patient portal.",
                })
              );
            } else {
              localStorage.setItem("patientData", JSON.stringify(res.data));
              localStorage.setItem("isPatientPortalData", "true");
              navigate(
                `${BASE_URL}/${resourceName}/${POD_URL}/${podId}/event/${eventId}/${CONTACT_DETAILS_URL}${window.location.search}`
              );
            }
          }
        } catch (err) {
          console.log(err);
        }
      } else {
        setShowPatientLogin(true);
      }
    }
  };

  const getProductInfoQuestionData = () => {
    const selectedProductIds = selectedServiceProductList.flatMap(
      (item) => item.productIds
    );
    const allProducts = servicesProductsOfCurrEvent.flatMap(
      (item) => item.products
    );
    const selectedProductsInfoQuestionIds = allProducts
      .filter((product) => selectedProductIds.includes(product.id))
      .flatMap((product) => product.infoQuestions)
      .filter((id) => id != null);

    dispatch(
      getProductInfoQuestions(
        Array.from(new Set(selectedProductsInfoQuestionIds || []))
      )
    );
  };

  const getSelectedProducts = (currServiceIdx: number) => {
    const serviceAndProducts = selectedServiceProductList.find(
      (serviceAndProducts) =>
        serviceAndProducts.serviceId ===
        servicesProductsOfCurrEvent[currServiceIdx].service.id
    );
    if (serviceAndProducts) {
      return serviceAndProducts.productIds;
    }
    return [];
  };

  const productChangeHandler = (productId: string, currServiceIdx: number) => {
    setSelectedServiceProductList((state) => {
      const newState = [...state];
      let isPresent = false;
      for (let i = 0; i < newState.length; i++) {
        const { serviceId, productIds } = newState[i];
        // Checking whether service already selected, if selected then add products.
        if (
          serviceId === servicesProductsOfCurrEvent[currServiceIdx].service.id
        ) {
          let serviceToBeEdited = { ...newState[i] };
          if (productIds.includes(productId)) {
            // Remove the product if it is present in service.
            const newProductList = productIds.filter(
              (proId) => proId !== productId
            );
            serviceToBeEdited.productIds = newProductList;
            newState[i] = serviceToBeEdited;
            // Remove the service if all its products are removed.
            if (newState[i].productIds.length === 0) {
              newState.splice(i, 1);
            }
          } else {
            // Add product if it is not present in service.
            const productIds = [...newState[i].productIds, productId];
            serviceToBeEdited.productIds = productIds;
            newState[i] = serviceToBeEdited;
          }
          isPresent = true;
          break;
        }
      }
      if (!isPresent) {
        newState.push({
          serviceId: servicesProductsOfCurrEvent[currServiceIdx].service.id,
          productIds: [productId],
        });
      }
      return newState;
    });
  };

  useEffect(() => {
    if (eventId && !servicesProductsOfCurrEvent.length) {
      const getEvent = async () => {
        try {
          const res = await fetchEvent(eventId);
          if (res.status === 200) {
            setEvent(res.data);
          }
        } catch (err) {
          console.log(err);
        }
      };
      getEvent();
      fetchEventRegistrationCountData([eventId]);
    }
  }, [eventId]);

  const fetchEventRegistrationCountData = async (eventIds: string[]) => {
    try {
      const registrationCountRes = await fetchEventRegistrationCount(eventIds);
      if (registrationCountRes.status) {
        setRegistrationCountData(registrationCountRes.data);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const getEventDay = () => {
    if (event?.id && day) {
      const date = new Date(event.startTime);
      date.setHours(0, 0, 0, 0);
      const eventStartTime = date.getTime();

      var numMillisInDay = 24 * 60 * 60 * 1000;
      var selectedDate = (Number(day) - 1) * numMillisInDay + eventStartTime;
      if (selectedDate > event.endTime) {
        return event.endTime;
      }
      return selectedDate;
    }
  };

  const getServicesAvailable = (event: Event) => {
    const date = new Date(event.startTime);
    date.setHours(0, 0, 0, 0);
    const eventStartTime = date.getTime();

    const currDate: Date = getEventDay()
      ? new Date(Number(getEventDay()))
      : new Date(eventStartTime);

    const data = registrationCountData?.find(
      (data) => data.eventId === event.id
    );

    if (data) {
      const eventDate =
        currDate.getTime() - new Date(currDate).getTimezoneOffset() * 60000;
      const servicesAvailable: SelectedService[] = [];

      event.services.forEach((service) => {
        const registration = data?.registrations?.find(
          (registration) =>
            registration?.serviceId === service.serviceId &&
            registration?.eventTime === eventDate
        );

        // Check if the service is available.
        // If no one signed up yet for the service, or
        // if the signup count is below the available capacity.
        if (!registration || registration?.signupCount < event.capacity) {
          servicesAvailable.push(service);
        }
      });

      return servicesAvailable;
    }
    return event.services;
  };

  useEffect(() => {
    if (
      event?.id &&
      servicesData.length &&
      !servicesProductsOfCurrEvent.length
    ) {
      const date = new Date(event.startTime);
      date.setHours(0, 0, 0, 0);
      const eventStartTime = date.getTime();

      const availableServices = getServicesAvailable(event);
      dispatch(
        setCurrEvent(event, getEventDay() || eventStartTime, availableServices)
      );
    }
  }, [event, registrationCountData, servicesData]);

  return (
    <>
      {showPatientLogin && (
        <PatientLogin
          onClose={() => {
            setShowPatientLogin(false);
          }}
          eventId={eventId}
          openSelectMember={(data) => {
            setShowMembers({
              show: true,
              data,
            });
            setShowPatientLogin(false);
          }}
        />
      )}
      {showMembers.show && (
        <SelectMember
          data={showMembers.data}
          eventId={eventId}
          onClose={() => setShowMembers({ show: false, data: [] })}
        />
      )}
      <Header />
      <div className={classes.main}>
        <div className={classes.topContainer}>
          <EventDetailsCard
            actions={
              <div className={classes.cardActions}>
                <PrimaryButton
                  disabled={
                    selectedServiceProductList.length > 0 ? false : true
                  }
                  name='PROCEED'
                  onClick={proceedHandler}
                />
              </div>
            }
            services={selectedServiceProductList}
          />
        </div>
        <div className={classes.bottomContainer}>
          {/* <Card>
          <FilterCard />
        </Card> */}
          <div className={classes.productContainer}>
            <div className={classes.heading}>Select Products</div>
            {services.map((service, currServiceIdx) => (
              <Card className={classes.card} key={service.id}>
                <div>
                  {servicesProductsOfCurrEvent[currServiceIdx]?.service
                    ?.name && (
                    <div className={classes.firstHeading}>
                      {
                        servicesProductsOfCurrEvent[currServiceIdx]?.service
                          ?.name
                      }
                    </div>
                  )}
                  <div className={classes.productCardContainer}>
                    {servicesProductsOfCurrEvent[currServiceIdx]?.products?.map(
                      (product, idx) => (
                        <ProductsCard
                          data={product}
                          added={getSelectedProducts(currServiceIdx).some(
                            (prodID) => product.id === prodID
                          )}
                          onAddRemove={(id: string) =>
                            productChangeHandler(id, currServiceIdx)
                          }
                          key={idx}
                        />
                      )
                    )}
                  </div>
                </div>
              </Card>
            ))}
            <div className={classes.proceedBtnContainer}>
              <PrimaryButton
                disabled={selectedServiceProductList.length > 0 ? false : true}
                name='PROCEED'
                onClick={proceedHandler}
                className={classes.proceedBtn}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProductSelection;
